import { CSSProperties, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import Skeleton from "react-loading-skeleton";
import { IconChevronLeft } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { CmsPageContent, extractCmsContent, Image } from "@shared/graphql";
import { Wrapper } from "../../components/layout/wrapper";
import { GalleryHandler, Overlay } from "../../components/layout/overlays";
import SeoMetas from "../../components/layout/seo-metas";
import RatioImage from "../../components/medias/ratio-image";
import "./item.scss";

const transDefs = defineMessages({
  seoTitle: { id: "makeovers.project.seo-title", defaultMessage: "Projet {name}" },
  back: { id: "makeovers.project.back", defaultMessage: "Revenir aux projets" },
  before: { id: "makeovers.project.before", defaultMessage: "Avant" },
  after: { id: "makeovers.project.after", defaultMessage: "Après" }
});

const SINGLE_PROJECT_PAGE_GQL_DATA = gql`
  query SingleCmsPage($pageId: ID!) {
    cms: cmsPage(pageId: $pageId) { data }
  }
`;

const MakeoverProjectPage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { id: pageId } = useParams();

  const galleryRef = useRef<GalleryHandler>(null);

  const { data, loading } = useQuery<{ cms: any }>(SINGLE_PROJECT_PAGE_GQL_DATA, {
    ssr: true,
    variables: { pageId }
  });

  const content = useMemo<CmsPageContent | undefined>(() => {
    if (!data?.cms) return undefined;
    return extractCmsContent(data.cms.data, locale);
  }, [ data, locale ]);

  const getSlideStyle = useCallback((picture: Image) => ({
    "--ccv-slide-ratio": picture.aspectRatio?.replace(":", " / ") || "3 / 2"
  } as CSSProperties), []);

  const onOpen = (index: number, type: "before" | "after") => {
    const items = (content![`${type}-items`] || []).map(({ picture }: any) => picture);
    items.length > 0 && galleryRef.current?.open(items, index);
  }

  const wrapperClasses = classNames("project-gallery-page");

  if (loading || !content)
    return (
      <main className={wrapperClasses}>
        <Wrapper.Centered className="summary">
          <h1><Skeleton width="80%" /></h1>
          <div className="richtext">
            <Skeleton count={3} />
            <Skeleton width="40%" />
          </div>
          <Skeleton containerClassName="nav-link" width={150} />
        </Wrapper.Centered>
      </main>);

  return (
    <main key={pageId} className={wrapperClasses}>

      <SeoMetas title={`${intl.formatMessage(transDefs.seoTitle, { name : content["summary"]["title"] })} | EV Real Estate`}
                description={content["summary"]["lead"] }
                image={content["summary"]["main-image"].assets.find((asset: any) => asset.breakpoint === "LG")?.url}
                keywords={content["seo-keywords"]?.split(",").map((k: string) => k.trim()) || []} />

      <Wrapper.Centered className="summary">
        <h1>{content["summary"]["title"]}</h1>
        {!!content["summary"]["body"] &&
          <div className="richtext" dangerouslySetInnerHTML={{ __html: content["summary"]["body"] }} />
        }
        <LocaleLink to="/renovation/makeovers/" className="nav-link">
          <IconChevronLeft className="n-icon" />
          {intl.formatMessage(transDefs.back)}
        </LocaleLink>
      </Wrapper.Centered>

      <Wrapper.ScrollableList as="section" className="gallery content">
        <Wrapper.ScrollableListHeader />
        <Wrapper.ScrollableListContent>
          {content["after-items"].map(({ picture }: any, index: number) => (
            <div key={index}
                 className="slide"
                 style={getSlideStyle(picture)}
                 onClick={() => onOpen(index, "after")}>
              <RatioImage source={picture.assets} alt={picture.alternativeText} />
            </div>)
          )}
        </Wrapper.ScrollableListContent>
      </Wrapper.ScrollableList>

      {!!content["before-items"]?.length &&
        <Wrapper.ScrollableList as="section" className="gallery content">
          <Wrapper.ScrollableListHeader title={intl.formatMessage(transDefs.before)} />
          <Wrapper.ScrollableListContent>
            {content["before-items"].map(({ picture }: any, index: number) => (
              <div key={index}
                   className="slide"
                   style={getSlideStyle(picture)}
                   onClick={() => onOpen(index, "before")}>
                <RatioImage source={picture.assets} alt={picture.alternativeText} />
              </div>)
            )}
          </Wrapper.ScrollableListContent>
        </Wrapper.ScrollableList>
      }

      <Overlay.Gallery ref={galleryRef} />

    </main>);

}

export default MakeoverProjectPage;
